import React, {useState} from "react";
import {observer} from "mobx-react-lite";
import {inject} from "mobx-react";
import {useCollapse} from 'react-collapsed'
import Countdown from 'react-countdown';
import moment from "moment";
import {pad} from "../helpers";

function TicketRow({DeviceStore, ticket, chooseVariants, chooseSeats, category, last = false}){

  const {t, locale, defaultLocale, theme, loading, popup, guestRequest} = DeviceStore;
  const {event} = DeviceStore.eventData;

  const name = ticket.name[locale] ?? ticket.name[defaultLocale];
  const description = ticket.description ? (ticket.description[locale] ?? ticket.description[defaultLocale]) : null;

  const [showImage, setShowImage] = useState(false);
  const [needsCollapse, setNeedsCollapse] = useState(false);
  const descriptionRef = React.useRef(null);

  const { getCollapseProps: getDesktopCollapseProps, getToggleProps: getDesktopToggleProps, isExpanded: isDesktopExpanded } = useCollapse();

  React.useEffect(() => {
    if (descriptionRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(descriptionRef.current).lineHeight);
      const height = descriptionRef.current.scrollHeight;
      const lines = Math.ceil(height / lineHeight);
      setNeedsCollapse(lines > 3);
    }
  }, [description]);

  const increase = () => {
    if(value >= ticket.userlimit ?? 25) return;
    if(ticket.usermin > value + ticket.increments){
      DeviceStore.updateTicketAmount(ticket.id, ticket.usermin)
    } else {
      DeviceStore.updateTicketAmount(ticket.id, value + ticket.increments)
    }
    DeviceStore.pixelAddToCart(ticket.name[locale], ticket.id, ticket.price)
  }

  const decrease = () => {
    if(!value) return;

    if(ticket.usermin > value - ticket.increments){
      DeviceStore.updateTicketAmount(ticket.id, 0)
    } else {
      DeviceStore.updateTicketAmount(ticket.id, value - ticket.increments)
    }
  }

  const value = DeviceStore.selectedTickets.filter(t => t.tickettype_id === ticket.id).length;

  if(!value && popup && category?.collapsed) return null;

  let ticketFee = guestRequest ? 0 : DeviceStore.eventData.event.customer_ticket_fee;

  const renderInput = ({minutes, seconds}) => {

    const time = moment().unix();

    if(ticket.sold_out){
      return (<p className="px-4 text-xs text-gray-500 w-28 bg-gray-100 border-gray-100 font-bold border flex p-1 rounded-3xl justify-center items-center text-center dark:bg-gray-950 dark:border-gray-950 dark:text-gray-400" style={{minHeight: '40px'}}>
        {ticket.sold_msg && ticket.sold_msg[locale] ?ticket.sold_msg[locale] : t('sold_out')}
      </p>)
    }

    if(ticket.sell_end && ticket.sell_end < time){
      return (<p className="px-4 font-bold text-xs w-28 text-gray-500 bg-gray-100 border-gray-100 border flex p-1 rounded-3xl justify-center items-center text-center dark:bg-gray-950 dark:border-gray-950 dark:text-gray-400" style={{minHeight: '40px'}}>
        {ticket.stopped_msg && ticket.stopped_msg[locale] ? ticket.stopped_msg[locale] : t('not_available')}
      </p>)
    }

    if(ticket.sell_start && ticket.sell_start > time){
      if(ticket.sell_start - time < 3600){
        return (
            <p className="px-4 text-gray-600 font-semibold rounded-3xl w-28 bg-gray-100 border-gray-100 border flex p-1 justify-center items-center text-center dark:bg-gray-950 dark:border-gray-950 dark:text-gray-400" style={{height: '40px'}}>
              {pad(minutes)}:{pad(seconds)}
            </p>
        );
      }

      return (<p className="px-4 text-xs font-bold text-gray-500 border-gray-100 border flex p-1 rounded-3xl justify-center items-center text-center dark:bg-gray-950 dark:border-gray-950 dark:text-gray-400" style={{minHeight: '40px'}}>
        {ticket.stopped_msg && ticket.stopped_msg[locale] ? ticket.stopped_msg[locale] : t('not_available')}
      </p>)
    }


    if(ticket.variants.length){
      const selected = DeviceStore.selectedTickets.filter(t => t.tickettype_id === ticket.id).length;

      if(!selected){
        return <button onClick={() => chooseVariants(ticket)} disabled={loading} className={"add-btn w-28 h-10 bg-blue-600 p-4 py-2 font-bold text-sm rounded-3xl " + (theme.primary_light ? 'text-black' : 'text-white')} style={{height: '42px', backgroundColor: theme?.primary_color}}>{t('choose_tickets')}</button>
      }


      return <div className="flex space-x-4 items-center">
        <button onClick={() => chooseVariants(ticket)} disabled={loading} className="flex items-center space-x-2 text-black border border-gray-300 w-28 justify-between p-4 py-2 rounded dark:border-gray-700 dark:bg-gray-950 dark:text-gray-300" style={{color: theme?.primary_color, borderColor: theme?.primary_color}}>
          <span>{selected + (selected === 1 ? ' ticket ' : ' tickets')}</span>
          <svg xmlns="http://www.w3.org/2000/svg"  style={{color: theme?.primary_color}} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>
    }

    if(ticket.seatplan_rank){
      const selectedAmount = DeviceStore.selectedTickets.filter(s => s.tickettype_id === ticket.id).length
      if(selectedAmount){
        return <div className="flex space-x-4 items-center">
          <button onClick={() => chooseSeats(ticket)} disabled={loading} className={'w-28 h-10 flex items-center justify-between space-x-2 border rounded-3xl border-gray-200 p-1 bg-gray-100 dark:bg-gray-950 dark:border-gray-700 dark:text-gray-400 ' + (loading ? ' opacity-20 cursor-not-allowed' : '')}>
            <span className="block whitespace-nowrap overflow-hidden text-gray-900 dark:text-gray-100 font-semibold text-center pl-2" style={{width: '80px'}}>{selectedAmount} <span className="text-xs"></span></span>

            <div>
              <span className={'bg-blue-600 rounded-3xl h-8 flex items-center justify-center w-8 ' + (theme.primary_light ? ' text-black ' : ' text-white ')} style={{backgroundColor: theme?.primary_color}}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3">
  <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32L19.513 8.2Z"/>
</svg>

            </span>
            </div>

          </button>
        </div>
      }
      return <button onClick={() => chooseSeats(ticket)} disabled={loading} className={"add-btn w-32 h-10 bg-blue-600 p-2 font-bold rounded-3xl text-sm truncate " + (loading ? ' opacity-20 cursor-not-allowed' : '') + (theme.primary_light ? 'text-black' : 'text-white')} style={{backgroundColor: theme?.primary_color}}>{t('choose_seats')}</button>
    }

    if(!value){
      return <button onClick={increase} disabled={loading} className={"add-btn w-28 h-10 bg-blue-600 p-2 font-bold rounded-3xl text-sm "  + (loading ? ' opacity-20 cursor-not-allowed ' : '') + (theme.primary_light ? ' text-black ' : ' text-white ')} style={{height: '42px', backgroundColor: theme?.primary_color}}>{t('choose_tickets')}</button>
    }

    const disabled = loading || value >= (ticket.userlimit ?? 25);

    return (
        <div className="flex items-center w-28">
          <div className="flex items-center bg-gray-100 rounded-3xl border border-gray-200 p-1 dark:bg-gray-950 dark:border-gray-700 dark:text-gray-400">
            <button onClick={decrease} disabled={loading} className="w-8 h-8 flex items-center justify-center rounded-3xl bg-white text-gray-600 hover:text-brand-gold hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-minus w-4 h-4" aria-hidden="true">
                <path d="M5 12h14"></path>
              </svg>
            </button>
            <span className="w-10 text-center font-semibold text-gray-900 dark:text-gray-100">{value}</span>
            <button onClick={increase} disabled={disabled} className={'w-8 h-8 flex items-center justify-center rounded-3xl bg-brand-gold  bg-blue-600 transition-colors shadow-sm' + (theme.primary_light ? ' text-black ' : ' text-white ') + (disabled ? ' opacity-50 cursor-not-allowed ' : '')} style={{backgroundColor: theme?.primary_color}}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus w-4 h-4" aria-hidden="true">
                <path d="M5 12h14"></path>
                <path d="M12 5v14"></path>
              </svg>
            </button>
          </div>
        </div>
    )
  }

  const showExFee = event.show_tickets_exfee;

  let ticketPrice = ticket.price + (ticket.price && !showExFee ? ticketFee : 0);

  if(guestRequest){
    ticketPrice = 0;
  }

  if(ticket.userlimit === ticket.usermin){
    ticketPrice *= ticket.userlimit;
    ticketFee *= ticket.userlimit;
  }

  return (
      <div className={'ticket-row ' + (ticket.sold_out ? 'opacity-40' : '') +' ticket-row-' + ticket.id}>

        {showImage ? <div className="fadeIn fixed top-0 right-0 bottom-0 left-0 bg-black bg-opacity-50 z-40 backdrop-blur-sm" onClick={() => setShowImage(false)}>
          <div className="max-w-2xl mx-auto h-full flex items-center">
            <img src={ticket.image} alt={name} className="w-full max-h-full object-contain"/>
          </div>
        </div> : null}

        <div className={"xsgrid grid-cols-6 sm:grid-cols-12 justify-between items-center p-4 md:p-6  border-gray-200 dark:border-gray-800 " + (last ? '' : 'border-b')}>
          <div className="col-span-6 flex">
            <div className="ticket-image hidden">
              <div onClick={() => ticket.image ? setShowImage(true) : null} className={'w-20 h-20 border dark:border-gray-800 rounded mr-4 ' + (ticket.image ? 'cursor-pointer' : '')} style={{backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: 'url(' + (ticket.image ? ticket.image : '/images/image_placeholder.png') + ')'}}></div>
            </div>
            <div>
              <h4 className="font-semibold text-md lg:text-lg pr-4 dark:text-white">
                <span className="md:cursor-default">{name}</span>
              </h4>
              {/* Desktop description only - hidden on mobile */}
              {description ? (
                description.length > 0 ? (
                  <div>
                    <p
                      ref={descriptionRef}
                      className="ticket-description prose ticket-description-desktop text-xs text-gray-600 pr-4 mt-1 dark:text-gray-400"
                      style={{
                        maxWidth: '500px',
                        whiteSpace: 'pre-line',
                        display: '-webkit-box',
                        WebkitLineClamp: isDesktopExpanded || !needsCollapse ? 'unset' : 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      dangerouslySetInnerHTML={{__html: description}}
                    >
                    </p>
                    {needsCollapse && (
                      <button
                        {...getDesktopToggleProps()}
                        className="text-xs font-semibold mt-1 hover:underline"
                        style={{ color: theme?.primary_color }}
                      >
                        {isDesktopExpanded ? t('show_less') : t('read_more')}
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="ticket-description ticket-description-desktop text-xs text-gray-600 pr-4 mt-1 hidden md:block dark:text-gray-400" style={{maxWidth: '500px', whiteSpace: 'pre-line'}}>
                    {description}
                  </p>
                )
              ) : null}
            </div>
          </div>
          <div className="flex justify-ticket items-center space-x-4 col-span-6">
            {guestRequest ? null : <div>
              <p className="ticket-price price-label font-semibold dark:text-gray-200">{new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(ticketPrice)}</p>
              {ticketFee && ticket.price ?
                  <p style={{fontSize: '9px', lineHeight: '12px'}} className="ticket-price-fee text-gray-600 dark:text-gray-400 text-right minw75">
                    {showExFee ? '+' : 'Incl.'} {new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(ticketFee)} fee
                  </p> : null
              }
            </div> }
            <div className="ticket-input">
              <Countdown
                  date={Math.max(ticket.sell_start * 1000, 1)}
                  renderer={renderInput}
              />
            </div>
          </div>
        </div>
      </div>
  )
}

export default inject("DeviceStore")(observer(TicketRow));
